/*
 * Suggesting who else is in a video, from its title and description.
 *
 * Shared by scripts/rescan-collaborators.js and the admin panel, so the
 * command line and the review screen cannot disagree about what counts as
 * evidence.
 *
 * Automatic detection gets you three videos out of a hundred here. That is not
 * a failure of the rules; it is what the source material supports. A YouTube
 * description is a credits list mixed with advertising, and telling "this
 * person was in the video" from "this person's channel is worth a follow"
 * needs a human far more often than not. So the job of this file is to sort
 * evidence into strong, weak and none, and let a person decide the middle.
 */

/*
 * Labels meaning the linked person is NOT a guest.
 *
 * "Follow me" is the one that matters most. A channel presented under a
 * person's own name links that person on every upload, and reading it as a
 * collaboration credited Eric Striffler as a guest on a hundred Pretty Much It
 * videos, which is his own channel.
 */
const CREW_OR_OWNER =
  /(follow me|my (instagram|twitter|tiktok)|edited by|editor|produced by|producer|filmed by|shot by|camera|music by|thumbnail|graphics|animation|patreon|merch|discord|sponsor|use code|business|booking|second channel|main channel)/i;

/* Labels that do say somebody took part. */
const REAL_CREDIT =
  /(guest|featuring|feat\.?|ft\.?|\bw\/|starring|joined by|co-?host|with me|special thanks|cast)/i;

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** What to look for, for one creator. */
function profileOf(creator) {
  const handles = new Set();
  const urls = [creator.channel, ...((creator.socialLinks || []).map((l) => l.url))].filter(Boolean);
  for (const url of urls) {
    const m = String(url).match(/\/@?([A-Za-z0-9._-]+)\/?$/);
    if (m && m[1].length >= 4) handles.add(m[1].toLowerCase());
  }
  return {
    id: creator.id,
    name: creator.name,
    key: norm(creator.name),
    handles: [...handles],
    // A short name matches too much to be evidence on its own.
    nameRe: String(creator.name).length >= 5 ? new RegExp(`\\b${escapeRe(creator.name)}\\b`, 'i') : null,
  };
}

export function buildProfiles(creators) {
  return creators.map(profileOf);
}

/** The line of a description mentioning a handle, so its label can be read. */
function lineContaining(text, handle) {
  const needle = handle.toLowerCase();
  for (const line of String(text).split('\n')) {
    const l = line.toLowerCase();
    if (l.includes(`@${needle}`) || l.includes(`/${needle}`)) return line;
  }
  return null;
}

/**
 * Who else might be in this video.
 *
 * Returns [{ creatorId, name, strength, why }], strongest first. `strength` is
 * 'strong' when the person is named in the title or credited as a guest, and
 * 'weak' when they are merely linked, which is usually a shoutout.
 */
export function suggestCollaborators({ title, description, channelTitle }, profiles, alreadyOn = []) {
  const have = new Set(alreadyOn);
  const own = norm(channelTitle);
  const text = `${title || ''}\n${description || ''}`;
  const titleLower = String(title || '').toLowerCase();
  const out = [];

  for (const p of profiles) {
    if (have.has(p.id)) continue;
    // The uploader is not a guest on their own video.
    if (p.key && p.key === own) continue;

    if (p.handles.some((h) => titleLower.includes(h)) || (p.nameRe && p.nameRe.test(title || ''))) {
      out.push({ creatorId: p.id, name: p.name, strength: 'strong', why: 'named in the title' });
      continue;
    }

    const line = p.handles.map((h) => lineContaining(text, h)).find((l) => l != null);
    if (line != null) {
      if (CREW_OR_OWNER.test(line)) continue;
      out.push({
        creatorId: p.id,
        name: p.name,
        strength: REAL_CREDIT.test(line) ? 'strong' : 'weak',
        why: line.trim().slice(0, 90),
      });
    } else if (p.nameRe && p.nameRe.test(text)) {
      out.push({ creatorId: p.id, name: p.name, strength: 'weak', why: 'name appears in the description' });
    }
  }

  return out.sort((a, b) => (a.strength === b.strength ? 0 : a.strength === 'strong' ? -1 : 1));
}
